// resources/js/src/components/community/comments/CommentItem.tsx
import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { timeAgo } from "./commentHelpers";
import PostReactions from "../post/components/PostReactions";
import { ReactionType } from "../post/interfaces/types";
import { useAuthStore } from "../../../../auth/auth.store";
import { Comment } from "../../../../services/types/comment.type";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

/* =========================================================
   Reactions List (avatars of users who reacted)
   ========================================================= */
type ReactionsListProps = {
  reactions: Comment["reactions"];
  maxVisible?: number;
};

const ReactionsList: React.FC<ReactionsListProps> = ({
  reactions,
  maxVisible = 3,
}) => {
  if (!reactions || reactions.length === 0) return null;

  const visible = reactions.slice(0, maxVisible);
  const remaining = reactions.length - maxVisible;

  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {visible.map((r) => (
          <img
            key={r.id}
            src={
              r.user.avatarUrl ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                r.user.name
              )}&background=10b981&color=fff&size=32&bold=true`
            }
            alt={r.user.name}
            title={`${r.user.name} (${r.type})`}
            className="w-5 h-5 rounded-full border-2 border-white dark:border-dark_grey object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                r.user.name
              )}&background=10b981&color=fff&size=32&bold=true`;
            }}
          />
        ))}
      </div>
      {remaining > 0 && (
        <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-1.5 font-medium">
          +{remaining}
        </span>
      )}
    </div>
  );
};

/* =========================================================
   CommentItem
   ========================================================= */
type Props = {
  comment: Comment;
  postId: number | string;
  onReply: (parentId: number, text: string) => Promise<boolean>;
  onEdit: (commentId: number, text: string) => Promise<boolean>;
  onDelete: (commentId: number) => Promise<boolean>;
  onReact: (commentId: number, reaction: ReactionType) => Promise<boolean>;
};

const CommentItem: React.FC<Props> = ({
  comment,
  postId,
  onReply,
  onEdit,
  onDelete,
  onReact,
}) => {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.body);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser);

  const isOwner =
    comment.isOwner ||
    currentUser?.id?.toString() === comment.author.id?.toString();

  // ============ Reactions ============
  const handleReact = async (r: ReactionType) => {
    if (!isAuthenticated) return;
    await onReact(comment.id, r);
  };

  // ============ Reply ============
  const submitReply = async () => {
    if (!isAuthenticated || !replyText.trim()) return;
    setSubmitting(true);
    const ok = await onReply(comment.id, replyText.trim());
    setSubmitting(false);
    if (ok) {
      setReplyText("");
      setShowReply(false);
    }
  };

  // ============ Edit ============
  const submitEdit = async () => {
    if (!editText.trim()) return;
    setSubmitting(true);
    const ok = await onEdit(comment.id, editText.trim());
    setSubmitting(false);
    if (ok) setIsEditing(false);
  };

  // ============ Delete ============
  const confirmDelete = async () => {
    setDeleting(true);
    const ok = await onDelete(comment.id);
    setDeleting(false);
    if (ok) {
      setShowDeleteDialog(false);
    }
  };

  return (
    <>
      <div className="flex gap-3">
        {/* Author Avatar */}
        <img
          src={
            comment.author.avatarUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              comment.author.name
            )}&background=10b981&color=fff&size=64&bold=true`
          }
          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          alt={comment.author.name}
          onError={(e) => {
            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
              comment.author.name
            )}&background=10b981&color=fff&size=64&bold=true`;
          }}
        />

        <div className="flex-1 min-w-0">
          {/* ==================== Body ==================== */}
          <div className="bg-charcoalGray/20 dark:bg-dark_grey rounded-xl px-3 py-2">
            <p className="text-sm dark:text-primary text-tealGreen font-semibold">
              {comment.author.name}
            </p>

            {isEditing ? (
              <div className="flex gap-2 mt-1">
                <input
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  disabled={submitting}
                  className="flex-1 rounded-full px-3 py-1 text-sm bg-charcoalGray dark:bg-slateGray focus:outline-none focus:ring-2 focus:ring-primary-500"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitEdit();
                    }
                  }}
                />
                <button
                  onClick={submitEdit}
                  disabled={submitting || !editText.trim()}
                  className="dark:text-primary text-tealGreen px-2 py-1 rounded hover:bg-slateGray/30 disabled:opacity-50 text-sm font-medium"
                >
                  {submitting ? "..." : "Save"}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditText(comment.body);
                  }}
                  disabled={submitting}
                  className="text-gray-500 px-2 py-1 rounded hover:bg-slateGray/30 disabled:opacity-50 text-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm break-words whitespace-pre-wrap">
                  {comment.body}
                </p>
                {comment.edited && (
                  <span className="text-[10px] text-gray-400 ml-1">
                    (edited)
                  </span>
                )}
              </>
            )}

            <p className="text-[10px] text-gray-900 dark:text-muted mt-1">
              {timeAgo(comment.createdAt)}
            </p>
          </div>

          {/* ==================== Actions Row ==================== */}
          <div className="flex items-center gap-3 text-xs mt-1 text-gray-500 flex-wrap">
            {/* Reactions Button + Avatars */}
            <div className="flex items-center gap-2">
              {isAuthenticated ? (
                <PostReactions
                  reaction={comment.myReaction}
                  onReact={handleReact}
                  col
                />
              ) : (
                <span className="opacity-50 pointer-events-none">
                  <PostReactions
                    reaction={comment.myReaction}
                    onReact={() => {}}
                    col
                  />
                </span>
              )}

              {/* ✅ Avatars of who reacted */}
              <ReactionsList reactions={comment.reactions} />
            </div>

            {/* Reply Button */}
            {isAuthenticated && (
              <button
                className="cursor-pointer hover:text-primary transition-colors flex items-center gap-1"
                onClick={() => setShowReply((v) => !v)}
              >
                <Icon icon="mdi:reply" className="w-4 h-4" />
                Reply
              </button>
            )}

            {/* Owner Actions */}
            {isOwner && (
              <>
                <button
                  className="cursor-pointer hover:text-primary transition-colors flex items-center gap-1"
                  onClick={() => {
                    setEditText(comment.body);
                    setIsEditing(true);
                  }}
                >
                  <Icon icon="mdi:pencil" className="w-4 h-4" />
                  Edit
                </button>
                <button
                  className="cursor-pointer hover:text-red-500 transition-colors flex items-center gap-1"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Icon icon="mdi:trash-can-outline" className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
          </div>

          {/* ==================== Reply Input ==================== */}
          {showReply && isAuthenticated && (
            <div className="mt-2 flex items-center gap-2">
              <input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                disabled={submitting}
                className="flex-1 rounded-full px-3 py-1 text-sm bg-gray-100 dark:bg-dark_grey focus:outline-none focus:ring-2 focus:ring-primary-500"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitReply();
                  }
                }}
              />
              <button
                onClick={submitReply}
                disabled={!replyText.trim() || submitting}
                className="text-primary disabled:opacity-40"
                aria-label="Send reply"
              >
                {submitting ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                ) : (
                  <Icon icon="mdi:send" className="w-4 h-4" />
                )}
              </button>
            </div>
          )}

          {/* ==================== Nested Replies ==================== */}
          {comment.replies?.length > 0 && (
            <div className="mt-3 ml-6 space-y-3">
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onReact={onReact}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        loading={deleting}
      />
    </>
  );
};

export default CommentItem;